import { createFileRoute } from "@tanstack/react-router"
/**
 * Practice Quiz Management Layout (T-PRACTICE-003)
 * Allows students to create practice quizzes with subject/topic filters
 * and take/retake them for self-study
 */

import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Progress,
  Stack,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { FaPlus, FaTrash } from "react-icons/fa"

import {
  type PracticeQuizCreate,
  type PracticeQuizPublic,
  PracticeQuizzesService,
  type RationaleModeEnum,
  type UserPublic,
} from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { SubjectSelector } from "@/components/Questions/SubjectSelector"
import { TopicSelector } from "@/components/Questions/TopicSelector"
import {
  DialogActionTrigger,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { Radio, RadioGroup } from "@/components/ui/radio"
import { toaster } from "@/components/ui/toaster"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { handleError } from "@/utils"

export const Route = createFileRoute("/_layout/practice-quizzes")({
  component: PracticeQuizzes,
})

export function PracticeQuizCreator() {
  const [isOpen, setIsOpen] = useState(false)
  const [quizName, setQuizName] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [selectedTopics, setSelectedTopics] = useState<
    Array<{ id: string; name: string }>
  >([])
  const [maxQuestions, setMaxQuestions] = useState(50)
  const [showRationaleMode, setShowRationaleMode] =
    useState<RationaleModeEnum>("after_each")
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()

  const createPracticeQuiz = useMutation({
    mutationFn: (data: PracticeQuizCreate) =>
      PracticeQuizzesService.createPracticeQuiz({ requestBody: data }),
    onSuccess: () => {
      showSuccessToast("Practice quiz created successfully.")
      queryClient.invalidateQueries({ queryKey: ["practiceQuizzes"] })
      handleClose()
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const handleSubmit = () => {
    if (!quizName.trim()) {
      toaster.create({
        title: "Validation Error",
        description: "Quiz name is required.",
        type: "error",
      })
      return
    }

    const student = queryClient.getQueryData<UserPublic>(["currentUser"])

    if (!student?.email) {
      toaster.create({
        title: "Account Error",
        description:
          "We couldn’t determine your identifier. Try signing in again.",
        type: "error",
      })
      return
    }

    if (maxQuestions < 1 || maxQuestions > 100) {
      toaster.create({
        title: "Validation Error",
        description: "Max questions must be between 1 and 100.",
        type: "error",
      })
      return
    }

    createPracticeQuiz.mutate({
      name: quizName,
      student_identifier: student.email,
      subject_ids: selectedSubjects.map((s) => s.id),
      topic_ids: selectedTopics.map((t) => t.id),
      max_questions: maxQuestions,
      show_rationale_mode: showRationaleMode,
    })
  }

  const handleClose = () => {
    setIsOpen(false)
    setQuizName("")
    setSelectedSubjects([])
    setSelectedTopics([])
    setMaxQuestions(50)
    setShowRationaleMode("after_each")
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={({ open }) => setIsOpen(open)}>
      <DialogTrigger asChild>
        <Button data-testid="practice-quiz-create-button">
          <FaPlus fontSize="16px" />
          Create Practice Quiz
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Practice Quiz</DialogTitle>
        </DialogHeader>

        <DialogCloseTrigger />

        <DialogBody>
          <VStack gap={4}>
            <Field label="Quiz Name" required>
              <Input
                data-testid="practice-quiz-name-input"
                value={quizName}
                onChange={(e) => setQuizName(e.target.value)}
                placeholder="Enter quiz name"
              />
            </Field>

            <Field label="Subjects (Optional)">
              <Box data-testid="practice-quiz-subject-selector">
                <SubjectSelector
                  selectedSubjects={selectedSubjects}
                  onChange={setSelectedSubjects}
                />
              </Box>
            </Field>

            <Field label="Topics (Optional)">
              <Box data-testid="practice-quiz-topic-selector">
                <TopicSelector
                  selectedTopics={selectedTopics}
                  onChange={setSelectedTopics}
                  allowCreate={false}
                />
              </Box>
            </Field>

            <Field
              label="Max Questions"
              helperText={`Select up to 100 questions. ${maxQuestions} question(s) will be randomly selected.`}
            >
              <Input
                data-testid="practice-quiz-max-input"
                type="number"
                value={maxQuestions}
                onChange={(e) => setMaxQuestions(Number(e.target.value))}
                min={1}
                max={100}
              />
            </Field>

            <Field label="Rationale Display">
              <RadioGroup
                colorPalette="blue"
                data-testid="practice-quiz-rationale-options"
                value={showRationaleMode}
                onValueChange={(event) =>
                  setShowRationaleMode(event.value as RationaleModeEnum)
                }
              >
                <VStack align="stretch">
                  <Radio value="after_each">After each question</Radio>
                  <Radio value="after_all">After quiz completion</Radio>
                </VStack>
              </RadioGroup>
            </Field>

            {createPracticeQuiz.isPending && (
              <Text color="gray.500">Creating practice quiz...</Text>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </DialogActionTrigger>
          <Button
            onClick={handleSubmit}
            disabled={createPracticeQuiz.isPending || maxQuestions > 100}
            colorPalette="blue"
            data-testid="practice-quiz-submit-button"
          >
            Create Quiz
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}

function PracticeQuizzesList() {
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const { showSuccessToast } = useCustomToast()

  const {
    data: practiceQuizzes,
    isLoading,
    isFetching,
  } = useQuery<PracticeQuizPublic[]>({
    queryKey: ["practiceQuizzes"],
    queryFn: () => PracticeQuizzesService.listPracticeQuizzes(),
    enabled: !!currentUser,
  })

  const deletePracticeQuiz = useMutation({
    mutationFn: (quizId: string) =>
      PracticeQuizzesService.deletePracticeQuiz({ quizId }),
    onSuccess: () => {
      showSuccessToast("Practice quiz deleted successfully.")
      queryClient.invalidateQueries({ queryKey: ["practiceQuizzes"] })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const quizzes = practiceQuizzes ?? []

  return (
    <VStack gap={4} alignItems="stretch" data-testid="practice-quizzes-list">
      {/* Loading indicator at the top */}
      {isFetching && (
        <Progress.Root value={null} size="xs" colorPalette="blue">
          <Progress.Track>
            <Progress.Range />
          </Progress.Track>
        </Progress.Root>
      )}

      {/* Show skeleton on initial load */}
      {isLoading ? (
        <PageSkeleton variant="table" rows={4} />
      ) : quizzes.length === 0 ? (
        <AppEmptyState
          title={emptyStateCopy.practiceQuizzes.none.title}
          description={emptyStateCopy.practiceQuizzes.none.description}
          data-testid="practice-quizzes-empty-state"
        />
      ) : (
        <>
          <Table.Root
            display={{ base: "none", md: "table" }}
            data-testid="practice-quizzes-table"
          >
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Quiz Name</Table.ColumnHeader>
                <Table.ColumnHeader>Questions</Table.ColumnHeader>
                <Table.ColumnHeader>Created</Table.ColumnHeader>
                <Table.ColumnHeader>Status</Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {quizzes.map((quiz) => (
                <Table.Row
                  key={quiz.id}
                  data-testid="practice-quiz-row"
                  data-quiz-id={quiz.id}
                >
                  <Table.Cell fontWeight="medium">{quiz.name}</Table.Cell>
                  <Table.Cell>
                    {quiz.questions?.length || 0} questions
                  </Table.Cell>
                  <Table.Cell>
                    {new Date(quiz.created_at).toLocaleDateString()}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette="green">Ready</Badge>
                  </Table.Cell>
                  <Table.Cell textAlign="right">
                    <Flex gap={2} justifyContent="flex-end">
                      <Button
                        data-testid="practice-quiz-take-button"
                        size="sm"
                        colorPalette="blue"
                        onClick={() => {
                          window.location.href = `/quizzes/${quiz.id}`
                        }}
                      >
                        Take Quiz
                      </Button>
                      <Button
                        data-testid="practice-quiz-delete-button"
                        size="sm"
                        variant="outline"
                        colorPalette="red"
                        onClick={() => deletePracticeQuiz.mutate(quiz.id)}
                        disabled={deletePracticeQuiz.isPending}
                      >
                        <FaTrash />
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <VStack
            gap={4}
            display={{ base: "flex", md: "none" }}
            data-testid="practice-quizzes-list-mobile"
          >
            {quizzes.map((quiz) => (
              <Box
                key={quiz.id}
                borderWidth="1px"
                borderRadius="lg"
                p={4}
                shadow="sm"
                bg="white"
                w="full"
                data-testid="practice-quiz-card"
                data-quiz-id={quiz.id}
              >
                <Stack gap={3}>
                  <Flex justify="space-between" align="center">
                    <Text fontWeight="semibold">{quiz.name}</Text>
                    <Badge colorPalette="green">Ready</Badge>
                  </Flex>

                  <Text fontSize="sm" color="gray.600">
                    {quiz.questions?.length || 0} question(s) -{" "}
                    {new Date(quiz.created_at).toLocaleDateString()}
                  </Text>

                  <Flex gap={2} justify="flex-end" wrap="wrap">
                    <Button
                      data-testid="practice-quiz-take-button"
                      size="sm"
                      colorPalette="blue"
                      onClick={() => {
                        window.location.href = `/quizzes/${quiz.id}`
                      }}
                    >
                      Take Quiz
                    </Button>
                    <Button
                      data-testid="practice-quiz-delete-button"
                      size="sm"
                      variant="outline"
                      colorPalette="red"
                      onClick={() => deletePracticeQuiz.mutate(quiz.id)}
                      disabled={deletePracticeQuiz.isPending}
                    >
                      <FaTrash />
                    </Button>
                  </Flex>
                </Stack>
              </Box>
            ))}
          </VStack>
        </>
      )}
    </VStack>
  )
}

function PracticeQuizzes() {
  const { practiceQuizzesLabel } = useStudentLabels()
  const breadcrumbs = useRouteBreadcrumbs("practice-quizzes.index")

  return (
    <PageShell
      title={practiceQuizzesLabel}
      description="Create targeted practice quizzes with selected subjects and topics."
      breadcrumbs={breadcrumbs}
      actions={<PracticeQuizCreator />}
      data-testid="practice-quizzes-page"
    >
      <VStack gap={6} alignItems="stretch">
        <Box
          p={4}
          borderWidth="1px"
          borderRadius="lg"
          bg="blue.50"
          borderColor="blue.200"
        >
          <Text fontSize="sm" color="blue.900">
            <strong>About Practice Quizzes:</strong> Choose up to 100 questions,
            decide when rationales show, and retake quizzes as often as you
            need.
          </Text>
        </Box>

        <PracticeQuizzesList />
      </VStack>
    </PageShell>
  )
}
